<template>
    <div>
        <field-string
            v-if="searchable"
            v-model="searchQuery"
            :label="$t('search')"
            class="app-list__search"
            :loading="searching"
            hide-details
            :autocomplete="false"
        >
            <template v-if="searchQuery" #append>
                <app-button icon="clear" icon-size="1rem" small @click="searchQuery = null" />
            </template>
        </field-string>

        <template v-if="searchable && searchQuery && !searching && !items.length">
            <p class="app-list__no-results">
                {{ $t('no_results_found') }}
            </p>
        </template>

        <template v-else>
            <template v-if="groupByTags">
                <template v-for="(group, groupName) in groups">
                    <div v-if="isGroupVisible(group)" :key="groupName">
                        <div class="overline text--secondary pl-4">{{ groupName }}</div>
                        <app-list-item
                            v-for="item in group"
                            :key="item.id"
                            :value="item"
                            :items="items"
                            @input="updateItem"
                        />
                        <app-divider hidden class="mb-4" />
                    </div>
                </template>
            </template>
            <template v-else>
                <app-list-item
                    v-for="(item, index) in items"
                    :key="item.id"
                    v-model="items[`${index}`]"
                    :items="items"
                />
            </template>
        </template>
    </div>
</template>

<script>
import AppListItem from '@/models/AppListItem';

export default {
    name: 'AppList',
    components: {
        AppListItem: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppListItem'
            )
    },
    props: {
        value: {
            type: Array,
            required: true,
            validator: items => {
                if (!Array.isArray(items)) return false;
                return items.every(item => item instanceof AppListItem);
            }
        },
        sort: {
            type: Boolean,
            default: false
        },
        groupByTags: {
            type: Boolean,
            default: false
        },
        otherGroupName: {
            type: String,
            default: 'Other'
        },
        searchable: {
            type: Boolean,
            default: false
        },
        searching: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        searchQuery: null
    }),
    computed: {
        items: {
            get() {
                let items = this.value;

                if (this.sort) {
                    items = items.sort((a, b) => a.name.localeCompare(b.name));
                }

                return items;
            },
            set(items) {
                this.$emit('input', items);
            }
        },
        groups() {
            let groupNames = [];

            let groups = this.items.reduce((groups, item) => {
                if (Array.isArray(item.tags) && item.tags.length > 0) {
                    for (const tag of item.tags) {
                        if (!(tag in groups)) {
                            groups[`${tag}`] = [];
                            groupNames.push(tag);
                        }
                        groups[`${tag}`].push(item);
                    }
                } else {
                    if (!(this.otherGroupName in groups)) {
                        groups[`${this.otherGroupName}`] = [];
                    }
                    groups[`${this.otherGroupName}`].push(item);
                }

                return groups;
            }, {});

            // Sort the groups by key name
            const sorted = groupNames.sort().reduce((acc, key) => {
                acc[`${key}`] = groups[`${key}`];
                return acc;
            }, {});

            // Add the other items to the end of the group list
            if (this.otherGroupName in groups) {
                sorted[this.otherGroupName] = groups[`${this.otherGroupName}`];
            }

            return sorted;
        }
    },
    watch: {
        async $route() {
            this.items = await Promise.all(this.items.map(item => item.setup(true)));
        },
        searchQuery(value) {
            this.searchDebounced(value);
        }
    },
    mounted() {
        this.searchDebounced = this._.debounce(this.search, 500);
    },
    methods: {
        search(searchQuery) {
            this.$emit('search', searchQuery);
        },
        isGroupVisible(group) {
            return group.some(item => !item.$hidden && item.authorised);
        },
        updateItem(value) {
            this.items = this.items.map(item => {
                if (item.id === value.id) {
                    item = value;
                }
                return item;
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.app-list {
    display: block;
    &__search {
        padding: 1rem;
    }
    &__no-results {
        padding: 0 1.4rem;
        margin-bottom: 0;
        font-size: 0.9rem;
    }
}
</style>
